import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Building2, CreditCard, Globe } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import BusinessProfileSettings from '@/components/SettingsTabs/BusinessProfileSettings';
import PaymentIntegrationSettings from '@/components/SettingsTabs/PaymentIntegrationSettings';


interface SettingsProps {
  isSidebarCollapsed?: boolean;
}

const Settings: React.FC<SettingsProps> = ({ isSidebarCollapsed = false }) => {
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = React.useState<string>('business');
  const [isFormDirty, setIsFormDirty] = useState(false);
  const submitHandlerRef = useRef<(() => void) | null>(null);

  // Set the active tab based on the URL parameter
  useEffect(() => {
    if (tabFromUrl && ['business', 'brand', 'payment', 'customer', 'regional'].includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);

  // Handle form state changes from BusinessProfileSettings
  const handleFormStateChange = (isDirty: boolean, submitHandler: () => void) => {
    setIsFormDirty(isDirty);
    submitHandlerRef.current = submitHandler;
  };

  // Handle save button click
  const handleSave = () => {
    if (submitHandlerRef.current) {
      submitHandlerRef.current();
    }
  };
  
  return (
<div
  className={`relative h-screen w-full flex flex-col transition-all duration-300 ${
    isSidebarCollapsed ? 'md:pl-10' : 'md:pl-24'
  }`}
>
  {/* Fixed Header */}
  <div className="py-6 z-10">
    <h1 className="text-3xl font-semibold">
      {activeTab === 'business' && 'Settings'}
      {activeTab === 'payment' && 'Stripe Integration Settings'}
      {activeTab === 'brand' && 'Brand Appearance Settings'}
      {activeTab === 'customer' && 'Customer Experience Settings'}
      {activeTab === 'regional' && 'Regional Settings'}
    </h1>
  </div>

  {/* Scrollable Content Wrapper */}
<div className="bg-white rounded-sm ">
  <main className="px-4 py-8">
    <div className="rounded-md h-[65vh]">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        {/* Tabs Header with Save Button */}
        <div className="flex items-center justify-between gap-4 mb-2">
          <TabsList className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 flex-1">
            <TabsTrigger value="business" className="flex items-center space-x-2">
              <Building2 className="h-4 w-4" />
              <span className="hidden sm:inline">Business Profile</span>
              <span className="inline sm:hidden">Profile</span>
            </TabsTrigger>
            <TabsTrigger value="payment" className="flex items-center space-x-2">
              <CreditCard className="h-4 w-4" />
              <span className="hidden sm:inline">Stripe Integration</span>
              <span className="inline sm:hidden">Payment</span>
            </TabsTrigger>
          </TabsList>
          {activeTab === 'business' && (
            <Button 
              onClick={handleSave}
              disabled={!isFormDirty}
              className="rounded-md"
            >
              Save Changes
            </Button>
          )}
        </div>

        {/* Tab Content - scrolls with wrapper */}
        <TabsContent value="business" className="p-6">
          <BusinessProfileSettings onFormStateChange={handleFormStateChange} />
        </TabsContent>
        <TabsContent value="payment" className="p-6">
          <PaymentIntegrationSettings />
        </TabsContent>
      </Tabs>
    </div>
  </main>
</div>
</div>
  );
};

export default Settings;